/**
 * Editor-Chat Synchronization Service
 * 
 * エディタとチャット間の状態同期を管理するサービス
 * 
 * 同期パターン:
 * 1. エディタ → チャット: AI生成状態の通知
 * 2. チャット → エディタ: ブロック編集の適用
 * 3. 双方向: コンテキスト情報の共有
 * 
 * 数学的モデル:
 * - 状態同期関数: sync: (S_editor × S_chat) → (S_editor' × S_chat')
 * - 因果関係保証: happened_before(e1, e2) ⇒ effect(e1) ≺ effect(e2)
 */

import { proxy, subscribe } from 'valtio';

import { logger } from '@/lib/logger';
import { chatStore } from '@/stores/chatStore';

import type { EditorOperation } from '@/lib/blocknote/apply-operations';
import type { PartialBlock } from '@/types';
import type { BlockOperationMetadata } from '@/types/block-operation';
import type { PageAgentOperation } from '@/types/chat';

/**
 * chat→editor 同期チャネルで「復元/AI 編集結果を editor へ適用」するコールバック型。
 * usePendingUpdateHandler / useEditorChatSync / BlockNoteEditor が同一形を共有するため、
 * 引数の追加(例: `revert`)で複数箇所が drift しないよう単一の SSOT に集約する。
 */
export type ApplyAIResultFn = (
  blocks: PartialBlock[],
  deletedBlockIds?: string[],
  metadata?: BlockOperationMetadata,
  pageId?: string,
  aiOperations?: EditorOperation[],
  revert?: boolean,
) => void;

// AI サジェストプレビューの型定義
export interface SuggestionPreview {
  pageId: string;
  blocks: PartialBlock[];
  proposalId: string;
  deletedBlockIds?: string[];
  operations?: readonly PageAgentOperation[];
  baseBlocksSnapshot?: readonly PartialBlock[];
  /** チップ切替など同一 proposalId で内容が変わった場合に区別するための世代番号 */
  version: number;
  /** 全 operation リスト（バッジ表示用） */
  allOperations?: readonly PageAgentOperation[];
  /** 選択済み operation インデックス（バッジの色分け用） */
  selectedIndices?: readonly number[];
}

export interface QueueSuggestionPreviewOptions {
  deletedBlockIds?: string[];
  operations?: readonly PageAgentOperation[];
  baseBlocksSnapshot?: readonly PartialBlock[];
  /** 全 operation リスト（バッジ表示用） */
  allOperations?: readonly PageAgentOperation[];
  /** 選択済み operation インデックス（バッジの色分け用） */
  selectedIndices?: readonly number[];
}

const SUGGESTION_APPROVAL_MESSAGE_TYPES = new Set([
  'block-approval',
  'bulk-page-edit-approval',
  'title-approval',
]);
const LOGGER_SCOPE = 'editor-chat-sync';

// 同期状態の型定義
export type ProposalResolutionStatus = 'approved' | 'rejected' | 'failed' | 'dismissed';

export interface SyncState {
  // エディタ状態
  editorState: 'uninitialized' | 'initialized' | 'editing' | 'ai_generating' | 'saving' | 'error';
  editorBlocks: PartialBlock[];
  editorPageId?: string;
  
  // チャット状態
  chatState: 'idle' | 'sending' | 'receiving' | 'error';
  chatPageId?: string;
  
  // 同期フラグ
  isAIGenerating: boolean;
  // キュー方式に変更して複数の更新を保持可能に
  // ページIDを含めて、異なるページへの更新を区別
  pendingEditorUpdates: Array<{
    pageId: string;
    blocks: PartialBlock[];
    deletedBlockIds?: string[];
    metadata?: BlockOperationMetadata;
    /** C4(案α): AI 編集 op 群。op-level 直接適用で AI が触った block だけ反映する (untouched/peer は不変) */
    aiOperations?: EditorOperation[];
    /**
     * turn snapshot rollback(取り消し)由来の更新。consumer は通常 blocks 空の更新を
     * no-op として無視するが、revert は「target(空ページ含む)へ live doc を op-level
     * reconcile する」意図なので、空 blocks でも必ず適用させる必要がある。
     */
    revert?: boolean;
    /**
     * durable-restore(Bug A 対策)由来の更新。consumer(usePendingUpdateHandler)は
     * collab 有効時に provider.transactWithAck(()=>applyBlocksOpLevel(...)) で 1
     * transaction 適用 + ack 待ちし、完了/失敗を restoreId キーで chat 側へ signal する。
     */
    restore?: { restoreId: string };
  }>;

  // AI サジェストプレビュー（エディタ上でインライン差分表示）
  pendingSuggestionPreview: SuggestionPreview | null;
  pendingSuggestionPreviews: SuggestionPreview[];

  // 外部（エディタバー/チャットカード）で承認・却下された proposal の状態
  resolvedProposals: Record<string, ProposalResolutionStatus>;

  // エディタ内バッジからの operation トグル要求
  pendingOperationToggle: { operationIndex: number; timestamp: number } | null;
}

// 同期ストア
export const syncStore = proxy<SyncState>({
  editorState: 'uninitialized',
  editorBlocks: [],
  chatState: 'idle',
  isAIGenerating: false,
  pendingEditorUpdates: [],
  pendingSuggestionPreview: null,
  pendingSuggestionPreviews: [],
  resolvedProposals: {},
  pendingOperationToggle: null,
});

// エディタ状態の更新
export function updateEditorState(
  state: SyncState['editorState'],
  blocks?: PartialBlock[],
  pageId?: string
): void {
  logger.debug(LOGGER_SCOPE, 'update_editor_state', {
    oldState: syncStore.editorState,
    newState: state,
    blocksCount: blocks?.length,
    pageId,
  });
  
  syncStore.editorState = state;
  if (blocks !== undefined) {
    syncStore.editorBlocks = blocks;
  }
  if (pageId !== undefined) {
    syncStore.editorPageId = pageId;
  }
  
  // AI生成状態の同期
  syncStore.isAIGenerating = state === 'ai_generating';
  
  // AI生成開始時にチャットに通知
  if (state === 'ai_generating') {
    chatStore.addMessage({
      id: `${Date.now()}-system`,
      type: 'ai',
      content: 'エディタでAI生成を開始しました...',
    });
  }
}

// チャット状態の更新
export function updateChatState(
  state: SyncState['chatState'],
  pageId?: string
): void {
  logger.debug(LOGGER_SCOPE, 'update_chat_state', {
    oldState: syncStore.chatState,
    newState: state,
    pageId,
  });
  
  syncStore.chatState = state;
  if (pageId !== undefined) {
    syncStore.chatPageId = pageId;
  }
}

// queue item に載せる metadata を決める。
// - anchor metadata(insertAfterBlockId / insertBeforeBlockId)はそのまま全 metadata を保持。
//   coalesce 判定(isFullSnapshotUpdate)が anchor の有無を見るため、anchor 付きは full
//   snapshot 扱いされず正しく全件適用される。
// - anchor が無くても toolCallId / aiRequestId だけは相関 metadata として残す。次回以降の
//   原因追跡(queue item と AI tool result / persist ログの突き合わせ)に効くため。anchor を
//   含めない相関 only metadata なので、hasAnchoredMetadata は引き続き false を返し coalesce
//   挙動は変わらない。
function selectQueuedMetadata(
  metadata?: BlockOperationMetadata
): BlockOperationMetadata | undefined {
  if (!metadata) return undefined;
  if (metadata.insertAfterBlockId || metadata.insertBeforeBlockId) return metadata;
  if (metadata.toolCallId || metadata.aiRequestId) {
    return { toolCallId: metadata.toolCallId, aiRequestId: metadata.aiRequestId };
  }
  return undefined;
}

// エディタへの編集適用をキュー
// pageIdを必須にして、異なるページへの更新を区別
export function queueEditorUpdate(
  pageId: string,
  blocks: PartialBlock[],
  deletedBlockIds?: string[],
  metadata?: BlockOperationMetadata,
  aiOperations?: EditorOperation[],
  revert?: boolean
): void {
  const queueLengthBefore = syncStore.pendingEditorUpdates.length;

  // Issue #1548: INFOレベルログ - キュー追加の詳細を記録
  logger.info(LOGGER_SCOPE, 'queue_editor_update', {
    pageId,
    toolCallId: metadata?.toolCallId,
    blocksCount: blocks.length,
    deletedBlockIds,
    hasMetadata: Boolean(metadata),
    revert: Boolean(revert),
    queueLengthBefore,
    timestamp: Date.now(),
  });

  // キューに追加（上書きではなく追加）。metadata 保持方針は selectQueuedMetadata を参照。
  const queuedMetadata = selectQueuedMetadata(metadata);

  syncStore.pendingEditorUpdates.push({
    pageId,
    blocks,
    deletedBlockIds,
    ...(queuedMetadata ? { metadata: queuedMetadata } : {}),
    ...(aiOperations && aiOperations.length > 0 ? { aiOperations } : {}),
    ...(revert ? { revert: true } : {}),
  });

  // Issue #1548: INFOレベルログ - キュー追加後の状態を記録
  logger.info(LOGGER_SCOPE, 'queue_editor_update_complete', {
    pageId,
    toolCallId: metadata?.toolCallId,
    queueLengthAfter: syncStore.pendingEditorUpdates.length,
    allQueuedPageIds: syncStore.pendingEditorUpdates.map(u => u.pageId),
  });
}

// ---------------------------------------------------------------------------
// durable-restore Promise registry (Bug A 対策)
//
// rollback の Y.js 復元を「1 transaction 適用 + persist-ack 待ち」で durable に
// するための module-level レジストリ。chat 側(useRollbackRestore)は restoreId で
// promise を await し、consumer(usePendingUpdateHandler)が適用結果を signal する。
// ---------------------------------------------------------------------------
interface DurableRestoreHandlers {
  resolve: () => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const durableRestoreRegistry = new Map<string, DurableRestoreHandlers>();

// consumer(usePendingUpdateHandler)が当該 pageId の restore item を consume しない
// (対象 editor 未マウント / 別ページ navigate 済み等)と promise が永久未解決になり
// rollback ボタンが busy 固着 + registry leak する。到達 timeout で必ず settle させる。
// transactWithAck(5s)+ macrotask 余裕を見て 15s。
const DURABLE_RESTORE_TIMEOUT_MS = 15000;

function generateRestoreId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `restore-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 復元 blocks を `restore:{restoreId}` 付き item として queue に積み、completion を
 * await できる promise を返す。consumer(usePendingUpdateHandler)が restoreId を
 * resolveDurableRestore / rejectDurableRestore で signal する。
 */
export function enqueueDurableRestore(
  pageId: string,
  blocks: PartialBlock[],
): { restoreId: string; promise: Promise<void> } {
  const restoreId = generateRestoreId();
  const promise = new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      if (durableRestoreRegistry.delete(restoreId)) {
        logger.warn(LOGGER_SCOPE, 'durable_restore_timeout', { pageId, restoreId });
        reject(new Error(`durable restore not consumed within ${DURABLE_RESTORE_TIMEOUT_MS}ms`));
      }
    }, DURABLE_RESTORE_TIMEOUT_MS);
    durableRestoreRegistry.set(restoreId, { resolve, reject, timer });
  });

  logger.info(LOGGER_SCOPE, 'enqueue_durable_restore', {
    pageId,
    restoreId,
    blocksCount: blocks.length,
  });

  syncStore.pendingEditorUpdates.push({
    pageId,
    blocks,
    restore: { restoreId },
  });

  return { restoreId, promise };
}

/** restoreId に対応する durable-restore promise を解決する(未知 id は no-op)。 */
export function resolveDurableRestore(restoreId: string): void {
  const handlers = durableRestoreRegistry.get(restoreId);
  if (!handlers) return;
  clearTimeout(handlers.timer);
  durableRestoreRegistry.delete(restoreId);
  handlers.resolve();
}

/** restoreId に対応する durable-restore promise を reject する(未知 id は no-op)。 */
export function rejectDurableRestore(restoreId: string, error: Error): void {
  const handlers = durableRestoreRegistry.get(restoreId);
  if (!handlers) return;
  clearTimeout(handlers.timer);
  durableRestoreRegistry.delete(restoreId);
  handlers.reject(error);
}

// 指定ページのペンディング中の編集を取得して削除
// 他のページへの更新はキューに残す
export function consumePendingEditorUpdates(pageId: string): Array<{
  pageId: string;
  blocks: PartialBlock[];
  deletedBlockIds?: string[];
  metadata?: BlockOperationMetadata;
  aiOperations?: EditorOperation[];
  revert?: boolean;
  restore?: { restoreId: string };
}> {
  const queueLengthBefore = syncStore.pendingEditorUpdates.length;
  const allPageIdsBefore = syncStore.pendingEditorUpdates.map(u => u.pageId);

  // 指定ページの更新のみを抽出
  const matchingUpdates = syncStore.pendingEditorUpdates.filter(
    update => update.pageId === pageId
  );
  // 他のページの更新はキューに残す
  syncStore.pendingEditorUpdates = syncStore.pendingEditorUpdates.filter(
    update => update.pageId !== pageId
  );

  // Issue #1548: INFOレベルログ - consume操作の詳細を記録
  logger.info(LOGGER_SCOPE, 'consume_pending_updates', {
    pageId,
    queueLengthBefore,
    allPageIdsBefore,
    consumedCount: matchingUpdates.length,
    consumedBlocksCounts: matchingUpdates.map(u => u.blocks.length),
    remainingCount: syncStore.pendingEditorUpdates.length,
    remainingPageIds: syncStore.pendingEditorUpdates.map(u => u.pageId),
    timestamp: Date.now(),
  });
  return matchingUpdates;
}

// ページコンテキストの同期確認
export function isPageContextSynced(): boolean {
  return syncStore.editorPageId === syncStore.chatPageId;
}

// 現在のエディタページIDを取得
export function getCurrentEditorPageId(): string | undefined {
  return syncStore.editorPageId;
}

// 現在のエディタコンテンツを取得
export function getCurrentEditorContent(): PartialBlock[] {
  return syncStore.editorBlocks;
}

function shouldSkipQueueingResolvedProposal(
  pageId: string,
  proposalId: string,
): boolean {
  const alreadyResolved = syncStore.resolvedProposals[proposalId];
  if (!alreadyResolved || alreadyResolved === 'dismissed') {
    return false;
  }

  logger.info(LOGGER_SCOPE, 'queue_suggestion_preview_skipped_resolved', {
    pageId,
    proposalId,
    resolution: alreadyResolved,
  });
  return true;
}

function hasDuplicateSuggestionPreview(proposalId: string): boolean {
  return (
    syncStore.pendingSuggestionPreview?.proposalId === proposalId ||
    syncStore.pendingSuggestionPreviews.some((preview) => preview.proposalId === proposalId)
  );
}

const deepClone = <T>(value: T): T => {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
};

/** チップ切替など同一 proposalId で内容更新時に区別するための世代カウンター */
let suggestionPreviewVersionCounter = 0;

function buildSuggestionPreview(
  pageId: string,
  blocks: PartialBlock[],
  proposalId: string,
  options?: QueueSuggestionPreviewOptions,
): SuggestionPreview {
  const deletedBlockIds = options?.deletedBlockIds;
  const operations = options?.operations;
  const baseBlocksSnapshot = options?.baseBlocksSnapshot;

  suggestionPreviewVersionCounter += 1;

  return {
    pageId,
    blocks: deepClone(blocks),
    proposalId,
    version: suggestionPreviewVersionCounter,
    ...(deletedBlockIds && deletedBlockIds.length > 0 ? { deletedBlockIds: [...deletedBlockIds] } : {}),
    ...(Array.isArray(operations) ? { operations: deepClone(operations) } : {}),
    ...(Array.isArray(baseBlocksSnapshot) ? { baseBlocksSnapshot: deepClone(baseBlocksSnapshot) } : {}),
    ...(Array.isArray(options?.allOperations) && options.allOperations.length > 0
      ? { allOperations: deepClone(options.allOperations) }
      : {}),
    ...(Array.isArray(options?.selectedIndices) ? { selectedIndices: [...options.selectedIndices] } : {}),
  };
}

function logSuggestionPreviewQueued(
  pageId: string,
  blocks: PartialBlock[],
  proposalId: string,
  options?: QueueSuggestionPreviewOptions,
): void {
  logger.info(LOGGER_SCOPE, 'queue_suggestion_preview', {
    pageId,
    blocksCount: blocks.length,
    deletedBlockIdsCount: options?.deletedBlockIds?.length ?? 0,
    operationCount: options?.operations?.length ?? 0,
    baseBlocksCount: options?.baseBlocksSnapshot?.length ?? 0,
    proposalId,
    hasActivePreview: Boolean(syncStore.pendingSuggestionPreview),
    queuedCount: syncStore.pendingSuggestionPreviews.length,
  });
}

// AI サジェストプレビューをキュー
export function queueSuggestionPreview(
  pageId: string,
  blocks: PartialBlock[],
  proposalId: string,
  options?: QueueSuggestionPreviewOptions,
): void {
  if (shouldSkipQueueingResolvedProposal(pageId, proposalId)) {
    return;
  }

  if (hasDuplicateSuggestionPreview(proposalId)) {
    logger.info(LOGGER_SCOPE, 'queue_suggestion_preview_skipped_duplicate', {
      pageId,
      proposalId,
    });
    return;
  }

  logSuggestionPreviewQueued(pageId, blocks, proposalId, options);
  const preview = buildSuggestionPreview(pageId, blocks, proposalId, options);

  if (!syncStore.pendingSuggestionPreview) {
    syncStore.pendingSuggestionPreview = preview;
    return;
  }

  syncStore.pendingSuggestionPreviews.push(preview);
}

// AI サジェストプレビューをクリア
export function clearSuggestionPreview(): void {
  logger.debug(LOGGER_SCOPE, 'clear_suggestion_preview', {
    activeProposalId: syncStore.pendingSuggestionPreview?.proposalId ?? null,
    queuedCount: syncStore.pendingSuggestionPreviews.length,
  });
  syncStore.pendingSuggestionPreview = null;

  while (syncStore.pendingSuggestionPreviews.length > 0) {
    const nextPreview = syncStore.pendingSuggestionPreviews.shift();
    if (!nextPreview) {
      break;
    }
    if (syncStore.resolvedProposals[nextPreview.proposalId]) {
      logger.debug(LOGGER_SCOPE, 'clear_suggestion_preview_skip_resolved_queue_item', {
        proposalId: nextPreview.proposalId,
      });
      continue;
    }
    syncStore.pendingSuggestionPreview = nextPreview;
    break;
  }
}

// Proposal の承認/却下状態を記録（チャットカード・エディタバー間の同期用）
export function markProposalResolved(
  proposalId: string,
  status: ProposalResolutionStatus,
): void {
  logger.info(LOGGER_SCOPE, 'mark_proposal_resolved', { proposalId, status });
  syncStore.resolvedProposals[proposalId] = status;

  // 'failed' はプレビュークリア目的のみ — チャットメッセージは変更しない
  // （handleSingleApprovalFailure 等が設定済みの error メッセージを保持するため）
  // 'dismissed' はプレビューの一時解除 — proposal 自体は未決定のままにする
  if (status === 'failed' || status === 'dismissed') {
    return;
  }

  const approvedAt = status === 'approved' ? new Date().toISOString() : undefined;
  for (const message of chatStore.messages) {
    if (!SUGGESTION_APPROVAL_MESSAGE_TYPES.has(message.type)) {
      continue;
    }
    if (message.proposalId !== proposalId) {
      continue;
    }
    chatStore.updateMessage(message.id, {
      approved: status === 'approved',
      approvedAt,
      error: undefined,
    });
  }
}

// 同期状態のリセット
export function resetSyncState(): void {
  syncStore.editorState = 'uninitialized';
  syncStore.editorBlocks = [];
  syncStore.editorPageId = undefined;
  syncStore.chatState = 'idle';
  syncStore.chatPageId = undefined;
  syncStore.isAIGenerating = false;
  syncStore.pendingEditorUpdates = [];
  syncStore.pendingSuggestionPreview = null;
  syncStore.pendingSuggestionPreviews = [];
  syncStore.resolvedProposals = {};
  syncStore.pendingOperationToggle = null;
  // 未消費の durable-restore を reject + clear して promise / timer / Map の leak を防ぐ
  // (ページ遷移・アンマウントで pendingEditorUpdates を捨てるため consumer が settle できない)。
  if (durableRestoreRegistry.size > 0) {
    const error = new Error('durable restore aborted: sync state reset');
    for (const handlers of durableRestoreRegistry.values()) {
      clearTimeout(handlers.timer);
      handlers.reject(error);
    }
    durableRestoreRegistry.clear();
  }
}

// デバッグ用: 現在の同期状態をログ出力
export function logSyncState(): void {
  if (process.env.NODE_ENV === 'development') {
    logger.debug('Editor-Chat Sync State:', {
      editorState: syncStore.editorState,
      editorBlocksCount: syncStore.editorBlocks.length,
      editorPageId: syncStore.editorPageId,
      chatState: syncStore.chatState,
      chatPageId: syncStore.chatPageId,
      isAIGenerating: syncStore.isAIGenerating,
      pendingUpdatesCount: syncStore.pendingEditorUpdates.length,
      isPageSynced: isPageContextSynced(),
    });
  }
}

// エクスポート: 状態購読用のヘルパー
export function subscribeToSyncState(callback: () => void): () => void {
  return subscribe(syncStore, callback);
}
